import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { Project } from '../../models/types';
import { ChevronRight, PlusCircle, Eye, Clock } from 'lucide-react';
import { toast } from 'react-toastify';

const ProjectList = () => {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsRef = collection(db, 'projects');
        
        // First try with compound query
        try {
          const q = query(
            projectsRef,
            where('userId', '==', currentUser.uid),
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          
          const querySnapshot = await getDocs(q);
          const projectList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate(),
            updatedAt: doc.data().updatedAt.toDate()
          })) as Project[];
          
          setProjects(projectList);
        } catch (indexError) {
          // If index error occurs, fall back to simple query
          console.warn('Index not found, falling back to simple query');
          const simpleQuery = query(
            projectsRef,
            where('userId', '==', currentUser.uid)
          );
          
          const querySnapshot = await getDocs(simpleQuery);
          const projectList = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate(),
            updatedAt: doc.data().updatedAt.toDate()
          })) as Project[];
          
          // Sort manually
          projectList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          setProjects(projectList);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error('Error loading projects');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [currentUser]);
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-700';
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'closed':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'Upcoming';
      case 'active':
        return 'Active';
      case 'closed':
        return 'Closed';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };
  
  const getTimelineLabel = (timeline: string) => {
    switch (timeline) {
      case '1':
        return '1 month';
      case '2-3':
        return '2-3 months';
      case '3-6':
        return '3-6 months';
      case '6+':
        return 'More than 6 months';
      case 'unknown':
        return 'Unknown';
      default:
        return timeline;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">My Projects</h1>
        <Link
          to="/project/create"
          className="bg-orange-500 text-white px-4 py-2 rounded-md font-medium hover:bg-orange-600 transition-colors flex items-center"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          New Project
        </Link>
      </div>
      
      {projects.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <h2 className="text-xl font-semibold mb-4">No projects yet</h2>
          <p className="text-gray-600 mb-6">
            Create your first project to start connecting with AI specialists.
          </p>
          <Link
            to="/project/create"
            className="bg-orange-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-600 transition-colors inline-flex items-center"
          >
            <PlusCircle className="h-5 w-5 mr-2" />
            Create First Project
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <h2 className="text-xl font-semibold">{project.name}</h2>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {getStatusLabel(project.status)}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-4 line-clamp-2">
                  {project.shortDescription}
                </p>
                
                <div className="flex justify-between items-center mb-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{getTimelineLabel(project.timeline)}</span>
                  </div>
                  <div className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    <span>{project.views}</span>
                  </div>
                </div>
                
                <Link
                  to={`/project/${project.id}`}
                  className="flex justify-center items-center w-full py-2 border border-orange-500 text-orange-500 rounded-md hover:bg-orange-50 transition-colors"
                >
                  View Details
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectList;